import { countFillGapBrackets, countMultipleChoiceShortBrackets, countFillGapsChoiceItems, parseChecklistBody } from './gapParsing.js';
import { countErrorCorrectionItems, countSortBoxesItems } from './sortBoxesAndErrorCorrection.js';

/** Types scored as a single Complete (no per-gap / per-item rewards). */
const FLAT_COMPLETE_TYPES = new Set([
    'text',
    'text_slides',
    'disappearing_text',
    'wordlist',
    'auto_wordlist',
    'wordlist_slides',
    'slides',
    'powerpoint_presentation',
    'sentence_slides',
    'randomizer',
    'reading',
]);

/**
 * True when the type uses per-gap / per-item scoring (`starsPerGap` / `coinsPerGap`).
 */
export function exerciseTypeUsesGapRewards(type) {
    if (!type || type === 'illustration') return false;
    return !FLAT_COMPLETE_TYPES.has(type);
}

/**
 * Default assessment when creating a new exercise of the given type.
 * - Gaps / item types: 1⭐ per gap, 3⭐ + 5🪙 on complete
 * - No gaps: 3⭐ + 2🪙 on complete
 * - Illustration: all rewards 0
 * - Other reward fields (per-gap coins, miss due date, …): 0
 */
export function getDefaultAssessmentForType(type) {
    const base = {
        mode: 'auto',
        pointsPerGap: 1,
        starsPerGap: 0,
        coinsPerGap: 0,
        starsOnComplete: 0,
        coinsOnComplete: 0,
        dueDate: null,
        starsOnMiss: 0,
        coinsOnMiss: 0,
    };
    if (type === 'illustration') return base;
    if (exerciseTypeUsesGapRewards(type)) {
        return { ...base, starsPerGap: 1, starsOnComplete: 3, coinsOnComplete: 5 };
    }
    return { ...base, starsOnComplete: 3, coinsOnComplete: 2 };
}

/**
 * Counts the number of rewardable items in an exercise based on its type and content.
 */
export function getExerciseTotalItems(exercise) {
    if (!exercise || !exercise.type) return 0;
    const body = exercise.body || '';

    switch (exercise.type) {
        case 'fill_gaps':
        case 'fill_gaps_self_check':
        case 'fill_gaps_ai':
        case 'fill_gaps_words':
            return countFillGapBrackets(body);

        case 'fill_gaps_choice':
            return countFillGapsChoiceItems(body);

        case 'order_words':
        case 'order_lines':
            // Count non-empty lines (since numbers are now optional)
            return body.split('\n').filter(line => line.trim()).length;

        case 'jumbled_letters':
        case 'dictation':
            // Count non-empty lines
            return body.split('\n').filter(line => line.trim()).length;

        case 'multiple_choice_short':
            return countMultipleChoiceShortBrackets(body);

        case 'multiple_choice_long':
            // Count questions: lines that are not options (starting with [)
            const lines = body.split('\n').filter(l => l.trim());
            let questionCount = 0;
            lines.forEach(line => {
                if (!line.trim().match(/^\[.+\]$/)) {
                    questionCount++;
                }
            });
            return questionCount;

        case 'image_fill_gaps':
        case 'image_matching':
        case 'matching_columns':
            // Count non-empty lines (each line = left // right  or  word // image_url)
            return body.split('\n').filter(line => line.trim()).length;

        case 'sort_boxes':
            return countSortBoxesItems(body);

        case 'error_correction':
            return countErrorCorrectionItems(body);

        case 'checklist':
            return parseChecklistBody(body).length;

        case 'essay':
            // Essays use pointsPerGap as the max score
            return parseFloat(exercise.assessment?.pointsPerGap) || 0;

        case 'voice_record':
            return 1;

        case 'voice_compare':
            return 1;

        case 'illustration':
            return 0;

        case 'text':
        case 'text_slides':
        case 'disappearing_text':
        case 'wordlist':
        case 'auto_wordlist':
        case 'wordlist_slides':
        case 'slides':
        case 'powerpoint_presentation':
        case 'sentence_slides':
        case 'randomizer':
            // These usually give a flat reward on completion (score 1/1)
            return 1;

        default:
            return 0;
    }
}

/**
 * Count unique true entries in an is_correct map (ignores duplicate "0" / 0 keys).
 */
export function countIsCorrectScore(isCorrect) {
    if (!isCorrect || typeof isCorrect !== 'object') return 0;
    const seen = new Set();
    let score = 0;
    for (const [k, v] of Object.entries(isCorrect)) {
        const n = Number(k);
        const canon = Number.isInteger(n) && String(n) === String(k) ? String(n) : String(k);
        if (seen.has(canon)) continue;
        seen.add(canon);
        if (v === true) score += 1;
    }
    return score;
}

/**
 * Ensure Online Lesson individual_states always carry live score/total for the progress bar.
 * Uses explicit fields when present; otherwise derives score from is_correct and total from the exercise.
 */
export function enrichLiveIndividualProgress(data, exercise) {
    if (!data || typeof data !== 'object') return data;

    const touchesProgress = (
        data.answers != null
        || data.locked != null
        || data.is_correct != null
        || data.status != null
        || data.score != null
        || data.total != null
    );
    if (!touchesProgress) return data;

    const out = { ...data };
    const exerciseTotal = exercise ? getExerciseTotalItems(exercise) : 0;
    const hasMap = out.is_correct && typeof out.is_correct === 'object';

    const explicitTotal = Number(out.total);
    if (Number.isFinite(explicitTotal) && explicitTotal > 0) {
        out.total = explicitTotal;
    } else if (exerciseTotal > 0) {
        out.total = exerciseTotal;
    }

    const explicitScore = Number(out.score);
    if (Number.isFinite(explicitScore) && explicitScore >= 0 && out.score !== '' && out.score != null) {
        out.score = explicitScore;
    } else if (hasMap) {
        out.score = countIsCorrectScore(out.is_correct);
    } else if (out.total != null && Number(out.total) > 0) {
        out.score = 0;
    }

    return out;
}

/**
 * Calculates the maximum potential rewards for a single exercise.
 */
export function getExerciseMaxRewards(exercise) {
    if (!exercise) return null;
    if (exercise.type === 'illustration') return null;
    const assessment = exercise.assessment || {};
    const totalItems = getExerciseTotalItems(exercise);

    const starsPerGap = parseFloat(assessment.starsPerGap) || 0;
    const coinsPerGap = parseInt(assessment.coinsPerGap, 10) || 0;
    const starsOnComplete = parseFloat(assessment.starsOnComplete) || 0;
    const coinsOnComplete = parseInt(assessment.coinsOnComplete, 10) || 0;

    const maxStars = (totalItems * starsPerGap) + starsOnComplete;
    const maxCoins = (totalItems * coinsPerGap) + coinsOnComplete;

    if (maxStars === 0 && maxCoins === 0) return null;

    return { stars: maxStars, coins: maxCoins };
}

/**
 * Calculates the total potential rewards for a block of exercises.
 */
export function getBlockMaxRewards(block, allExercises) {
    if (!block || !block.exercise_ids || !allExercises) return null;
    
    let totalStars = 0;
    let totalCoins = 0;

    block.exercise_ids.forEach(eid => {
        const exercise = allExercises.find(ex => ex.id === eid);
        if (exercise) {
            const rewards = getExerciseMaxRewards(exercise);
            if (rewards) {
                totalStars += rewards.stars;
                totalCoins += rewards.coins;
            }
        }
    });

    if (totalStars === 0 && totalCoins === 0) return null;

    return { stars: totalStars, coins: totalCoins };
}

/**
 * Converts a raw correct-item count into a 0–100 percentage.
 * Prefer an explicit `total`; otherwise fall back to the exercise's item count.
 */
export function getScorePercent(score, total, exercise = null) {
    if (score == null || Number.isNaN(Number(score))) return null;
    let t = total;
    if (t == null || Number(t) <= 0) {
        t = exercise ? getExerciseTotalItems(exercise) : 0;
    }
    if (!t || Number(t) <= 0) return null;
    return Math.round((Number(score) / Number(t)) * 100);
}

/** Percent for one block sub-status (`score` / `total`, with exercise fallback). */
export function getSubStatusPercent(subStat, exercise = null) {
    if (!subStat || subStat.score == null) return null;
    return getScorePercent(subStat.score, subStat.total, exercise);
}

/**
 * Average of sub-exercise percentages for a block assignment.
 * Returns null when no scored sub-exercises are available.
 */
export function getBlockAveragePercent(subStatuses, exercises) {
    if (!exercises?.length) return null;
    const percents = exercises
        .map((ex) => {
            const st = subStatuses?.[ex.id] ?? subStatuses?.[String(ex.id)];
            return getSubStatusPercent(st, ex);
        })
        .filter((p) => p != null);
    if (!percents.length) return null;
    return Math.round(percents.reduce((a, b) => a + b, 0) / percents.length);
}

/**
 * Formats a reward object into a user-friendly string.
 * Example: "5 ⭐ 10 coins", "5 ⭐", "10 coins", or "No rewards"
 */
export function formatRewards(rewards) {
    if (!rewards || (rewards.stars === 0 && rewards.coins === 0)) {
        return "No rewards";
    }

    const parts = [];
    if (rewards.stars > 0) {
        parts.push(`${rewards.stars} ⭐`);
    }
    if (rewards.coins > 0) {
        parts.push(`${rewards.coins} coins`);
    }

    return parts.length > 0 ? parts.join(' ') : "No rewards";
}
